import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useOutletContext } from 'react-router-dom';
import { ProductionOutletType } from '..';
import { TypeMainOutlet } from '../..';
import { GlobalTable, HeaderLine, Td, Th, Tr, ConfigButton, FooterTr } from '../../../../components/styles/table-style';
import { Settings } from 'lucide-react';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import ApiService from '../../../../services/ApiService';
import { format } from 'date-fns';
import TableFiltersContext from '../../../../contexts/TableFiltersContext';

interface ProductionMovement {
  id: number;
  ploomesOrderId: number;
  rawMaterialName: string;
  status: string;
  quantity: number;
  oldTotalWeight: number;
  newTotalWeight: number;
  oldLengthInMm: number;
  newLengthInMm: number;
  createdAt: string;
  orderName?: string;
}

const ProductionMovementTable: React.FC = () => {
  const { setTabIndex, fields, quantity, setQuantity, refresh, setIsDropdownOpen } = useOutletContext<ProductionOutletType>();
  const { search } = useOutletContext<TypeMainOutlet>();
  const { movementFilters, movementTypeFilter } = useContext(TableFiltersContext);
  const [movements, setMovements] = useState<ProductionMovement[]>([]);
  const [totalMovements, setTotalMovements] = useState<number | null>(null);

  const normalizeMovements = (raw: any): ProductionMovement[] => {
    if (Array.isArray(raw)) {
      return raw as ProductionMovement[];
    }
    if (raw?.data && Array.isArray(raw.data)) {
      return raw.data as ProductionMovement[];
    }
    if (raw?.items && Array.isArray(raw.items)) {
      return raw.items as ProductionMovement[];
    }
    if (raw?.results && Array.isArray(raw.results)) {
      return raw.results as ProductionMovement[];
    }

    if (raw && typeof raw === 'object') {
      const firstArray = Object.values(raw).find((value) => Array.isArray(value)) as ProductionMovement[] | undefined;
      if (Array.isArray(firstArray)) {
        return firstArray;
      }
    }

    return [];
  };

  useEffect(() => {
    setTabIndex(1);
  }, [setTabIndex]);

  useEffect(() => {
    const fetchMovements = async () => {
      try {
        const parameters = {
          size: quantity,
          filters: movementFilters,
          global: search,
          movementType: movementTypeFilter
        };

        const response = await ApiService.movements.find(parameters);
        const rawData = response?.data;
        const extractedMovements = normalizeMovements(rawData);

        setMovements(extractedMovements);
      } catch (error) {
        console.error('Erro ao buscar movimentações de produção:', error);
      }
    };

    fetchMovements();
  }, [quantity, movementFilters, search, movementTypeFilter, refresh]);

  useEffect(() => {
    const fetchTotalMovements = async () => {
      try {
        const parameters = {
          size: 'All',
          filters: movementFilters,
          global: search,
          movementType: movementTypeFilter
        };

        const response = await ApiService.movements.find(parameters);
        const rawData = response?.data;
        const allMovements = normalizeMovements(rawData);

        if (Array.isArray(allMovements)) {
          setTotalMovements(allMovements.length);
          return;
        }

        const possibleTotals: Array<number | string | undefined> = [
          rawData?.total,
          rawData?.count,
          rawData?.meta?.total,
          rawData?.meta?.count,
          response?.headers?.['x-total-count'],
        ];

        const resolvedTotal = possibleTotals.reduce<number | null>((acc, candidate) => {
          if (acc !== null) return acc;
          if (typeof candidate === 'number' && !Number.isNaN(candidate)) {
            return candidate;
          }
          if (typeof candidate === 'string' && candidate.trim().length > 0) {
            const parsed = Number(candidate);
            if (!Number.isNaN(parsed)) {
              return parsed;
            }
          }
          return acc;
        }, null);

        setTotalMovements(resolvedTotal);
      } catch (error) {
        console.error('Erro ao buscar total de movimentações:', error);
        setTotalMovements(null);
      }
    };

    fetchTotalMovements();
  }, [movementFilters, movementTypeFilter, search, refresh]);

  const formatNumber = (value: number) => {
    if (typeof value !== 'number') return '-';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const filteredData = (item: ProductionMovement) => {
    if (movementTypeFilter === "Manual") {
      return item.ploomesOrderId === 0 || item.ploomesOrderId === 1;
    }
    if (movementTypeFilter === "Pedido") {
      return item.ploomesOrderId !== 0 && item.ploomesOrderId !== 1;
    }
    return true;
  };

  const hasMore = useMemo(() => {
    if (totalMovements !== null) {
      return movements.length < totalMovements;
    }
    return movements.length >= quantity;
  }, [movements.length, quantity, totalMovements]);

  return (
    <StyledTable>
      <thead>
        <HeaderLine>
          {fields?.whichFieldsToShow?.filter(field => field.checked).map((field, index) => (
            <Th key={index}>{field.fieldName}</Th>
          )) || (
              <>
                <Th>Pedido</Th>
                <Th>Matéria prima</Th>
                <Th>Status</Th>
                <Th>Quantidade</Th>
                <Th>Peso anterior</Th>
                <Th>Peso novo</Th>
                <Th>Comp. anterior</Th>
                <Th>Comp. novo</Th>
                <Th>Data</Th>
              </>
            )}
          <ConfigButton onClick={() => setIsDropdownOpen(prev => !prev)}>
            <Settings />
          </ConfigButton>
        </HeaderLine>
      </thead>
      <tbody>
        {movements.filter(filteredData).map((item) => (
          <Tr key={item.id}>
            {fields?.whichFieldsToShow?.filter(field => field.checked).map((field, index) => (
              <Td key={index}>
                {field.field === 'createdAt' ? (
                  format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm') + 'h'
                ) : field.field === 'ploomesOrderId' ? (
                  item.orderName || item.ploomesOrderId
                ) : typeof item[field.field as keyof ProductionMovement] === 'number' ? (
                  formatNumber(item[field.field as keyof ProductionMovement] as number)
                ) : field.field === 'status' ? (
                  item[field.field] === '410' ? 'Desfeito' : 'Abatido'
                ) : (
                  item[field.field as keyof ProductionMovement]
                )}
              </Td>
            )) || (
                <>
                  <Td>{item.orderName || item.ploomesOrderId}</Td>
                  <Td>{item.rawMaterialName}</Td>
                  <Td>{item.status === '410' ? 'Desfeito' : 'Abatido'}</Td>
                  <Td>{formatNumber(item.quantity)}</Td>
                  <Td>{formatNumber(item.oldTotalWeight)}</Td>
                  <Td>{formatNumber(item.newTotalWeight)}</Td>
                  <Td>{formatNumber(item.oldLengthInMm)}</Td>
                  <Td>{formatNumber(item.newLengthInMm)}</Td>
                  <Td>{format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm')}h</Td>
                </>
              )}
          </Tr>
        ))}
        {movements.length === 0 && (
          <Tr>
            <Td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
              Nenhuma movimentação encontrada
            </Td>
          </Tr>
        )}
      </tbody>
      <tfoot>
        <FooterTr>
          <Td
            colSpan={fields?.whichFieldsToShow?.filter(field => field.checked).length || 9}
            style={{ textAlign: 'center', background: 'transparent', padding: '1rem 0', width: '100%' }}
          >
            <LoadMoreButton
              currentCount={movements.length}
              totalCount={totalMovements ?? undefined}
              onLoadMore={() => setQuantity((q) => q + 10)}
              hasMore={hasMore}
            />
          </Td>
        </FooterTr>
      </tfoot>
    </StyledTable>
  );
};

const StyledTable = styled(GlobalTable)`
  width: 100%;
  min-width: 100%;
  margin: 0 auto;
  border-collapse: collapse;
`;

export default ProductionMovementTable;